// File location: components/client/hooks/useClientForm.ts

import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
	ClientCreateRequest,
	ClientUpdateRequest,
	CommunicationChannel,
	FreelancerFrequency,
	Client
} from "../../../models/Client";
import { BusinessDomain } from "../../../models/BusinessDomain";
import { UserType } from "../../../models/UserType";
import { UserRole } from "../../../models/UserRole";
import { useAuthProvider } from "../../../auth/hooks/auth-provider";
import { Company } from "../../../models/Company";
import {
	useClient,
	useCreateClient,
	useUpdateClient,
	useFreelancerExpertises,
	useClientOnboardingTools,
	useBusinessDomains,
	useCommunicationChannels,
	useFreelancerFrequencies
} from "../../../hooks/useClient";
import {
	useSearchCompaniesAutocomplete,
	useUserCreatedCompaniesCount
} from "../../../hooks/useCompany";
import { PhoneNumberUtil } from "google-libphonenumber";

// Phone validation function using google-libphonenumber
const phoneUtil = PhoneNumberUtil.getInstance();
const isPhoneValid = (phone: string) => {
	// Handle empty or null phone numbers
	if (!phone || phone.trim() === "" || (phone.startsWith("+") && phone.length <= 4)) {
		return false; // Phone is required
	}

	try {
		return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
	} catch (error) {
		return false;
	}
};

// Helper function to check if phone number is effectively empty (just country code)
const isPhoneEmpty = (phone: string) => {
	return !phone || phone.trim() === "" || (phone.startsWith("+") && phone.length <= 4);
};

interface UseClientFormProps {
	isOnboarding?: boolean;
	clientToEdit?: Client;
}

export const useClientForm = ({
	isOnboarding = false,
	clientToEdit
}: UseClientFormProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { user } = useAuthProvider();

	// API hooks
	const { data: clientProfile, isLoading: isLoadingProfile } = useClient();
	const createClientProfileMutation = useCreateClient();
	const updateClientProfileMutation = useUpdateClient();
	const { data: expertises, isLoading: isLoadingExpertises } = useFreelancerExpertises();
	const { data: tools, isLoading: isLoadingTools } = useClientOnboardingTools();
	const { data: businessDomains, isLoading: isLoadingBusinessDomains } =
		useBusinessDomains();
	const { data: communicationChannels, isLoading: isLoadingChannels } =
		useCommunicationChannels();
	const { data: frequencies, isLoading: isLoadingFrequencies } =
		useFreelancerFrequencies();

	// Hook to get user's created companies count (for limiting to 1 per client)
	const { data: userCreatedCompaniesCount = 0, isLoading: isLoadingCompaniesCount } =
		useUserCreatedCompaniesCount();

	// State for company search
	const [companySearchTerm, setCompanySearchTerm] = useState("");
	const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

	// Company search hook
	const { data: searchedCompanies = [], isLoading: isSearchingCompanies } =
		useSearchCompaniesAutocomplete(companySearchTerm);

	// Ref for storing the created company id after company creation
	const createdCompanyIdRef = useRef<number | null>(null);

	const [dataLoadingComplete, setDataLoadingComplete] = useState(false);

	// Form state
	const [jobTitle, setJobTitle] = useState("");
	const [companyId, setCompanyId] = useState<number | null>(null);
	const [phoneNumber, setPhoneNumber] = useState("");
	const [selectedFrequency, setSelectedFrequency] = useState<FreelancerFrequency | "">(
		""
	);
	const [usesPlatforms, setUsesPlatforms] = useState<boolean | undefined>(undefined);
	const [selectedCommunicationChannels, setSelectedCommunicationChannels] = useState<
		CommunicationChannel[]
	>([]);
	const [linkedInUrl, setLinkedInUrl] = useState("");

	// Multi-select fields
	const [selectedExpertises, setSelectedExpertises] = useState<number[]>([]);
	const [selectedDomains, setSelectedDomains] = useState<BusinessDomain[]>([]);
	const [selectedTools, setSelectedTools] = useState<number[]>([]);

	// Track if client record exists
	const [clientExists, setClientExists] = useState(false);

	// UI state
	const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [alertOpen, setAlertOpen] = useState(false);
	const [alertMessage, setAlertMessage] = useState("");
	const [alertSeverity, setAlertSeverity] = useState<"success" | "error">("success");

	// Check if phone fields are required based on selected communication channels
	const isPhoneRequired = selectedCommunicationChannels.some(
		channel =>
			channel === CommunicationChannel.SMS ||
			channel === CommunicationChannel.Phone ||
			channel === CommunicationChannel.WhatsApp
	);

	// Overall loading state
	const isLoading =
		isLoadingProfile ||
		isLoadingExpertises ||
		isLoadingTools ||
		isLoadingBusinessDomains ||
		isLoadingChannels ||
		isLoadingFrequencies ||
		isLoadingCompaniesCount;

	// Flag for mutations in progress
	const isMutating =
		createClientProfileMutation.isPending || updateClientProfileMutation.isPending;

	// Check if user is admin, CSM or TAM (internal users)
	const isInternalUser =
		user?.type === UserType.Internal &&
		user?.roles?.some(role =>
			[UserRole.Admin, UserRole.CSM, UserRole.TAM].includes(role as UserRole)
		);

	// Determine if user is allowed to change company
	// Client users can change company if they don't have one yet
	// Internal users (Admin, CSM, TAM) can always change company
	// If companyId is already set (e.g. via project invitation), field is read-only even during onboarding
	const canChangeCompany = !companyId || !clientExists || isInternalUser;

	// Determine if user is allowed to create a new company
	// Client users can only create ONE company (security rule)
	// Internal users (Admin, CSM, TAM) can create unlimited companies
	const hasCreatedCompany = userCreatedCompaniesCount > 0;
	const canCreateNewCompany =
		isInternalUser || (!hasCreatedCompany && user?.type === UserType.Client);

	// Check if all data is loaded
	useEffect(() => {
		if (
			!isLoading &&
			expertises &&
			tools &&
			businessDomains &&
			communicationChannels &&
			frequencies
		) {
			setDataLoadingComplete(true);
		}
	}, [isLoading, expertises, tools, businessDomains, communicationChannels, frequencies]);

	// If a profile exists (either from current user or clientToEdit), populate form with existing data
	useEffect(() => {
		const profileToUse = clientToEdit || clientProfile;

		if (profileToUse && dataLoadingComplete) {
			// Populate form with profile data
			setJobTitle(profileToUse.jobTitle || "");
			// Set company data
			if (profileToUse.companyId && profileToUse.company) {
				setCompanyId(profileToUse.companyId);
				setSelectedCompany(profileToUse.company);
			}
			// Set phoneNumber from profile data
			setPhoneNumber(profileToUse.phoneNumber || "");
			setSelectedFrequency(profileToUse.freelancerFrequency || "");
			setUsesPlatforms(profileToUse.usesPlatforms);
			// Set communication channels
			if (
				profileToUse.preferredCommunicationChannels &&
				profileToUse.preferredCommunicationChannels.length > 0
			) {
				setSelectedCommunicationChannels(profileToUse.preferredCommunicationChannels);
			}
			setLinkedInUrl(profileToUse.linkedInUrl || "");

			// Set multi-select fields from existing data
			if (
				profileToUse.freelancerExpertises &&
				profileToUse.freelancerExpertises.length > 0
			) {
				setSelectedExpertises(profileToUse.freelancerExpertises.map(exp => exp.id));
			}

			if (profileToUse.businessDomains && profileToUse.businessDomains.length > 0) {
				setSelectedDomains(profileToUse.businessDomains);
			}

			if (profileToUse.tools && profileToUse.tools.length > 0) {
				setSelectedTools(profileToUse.tools.map(tool => tool.id));
			}

			setClientExists(true);
		}
	}, [clientProfile, clientToEdit, dataLoadingComplete]);

	// Validate the form
	const validateForm = (isDraft: boolean = false): boolean => {
		const newErrors: Record<string, string> = {};

		// For draft save, only validate LinkedIn URL if LinkedIn is selected
		if (isDraft) {
			if (
				selectedCommunicationChannels.includes(CommunicationChannel.LinkedInMessaging)
			) {
				if (!linkedInUrl) {
					newErrors.linkedInUrl = t("validation.required");
				} else if (!linkedInUrl.includes("linkedin.com")) {
					newErrors.linkedInUrl = t("validation.invalidLinkedInUrl");
				}
			}
			setErrors(newErrors);
			return Object.keys(newErrors).length === 0;
		}

		// Required fields for final submission
		if (!jobTitle) {
			newErrors.jobTitle = t("validation.required");
		}

		if (!companyId) {
			newErrors.companyId = t("validation.required");
		}

		if (!selectedFrequency) {
			newErrors.freelancerFrequency = t("validation.required");
		}

		if (selectedExpertises.length === 0) {
			newErrors.freelancerExpertises = t("validation.required");
		}

		if (selectedDomains.length === 0) {
			newErrors.businessDomains = t("validation.required");
		}

		if (selectedCommunicationChannels.length === 0) {
			newErrors.communicationChannels = t("validation.required");
		}

		// Validate usesPlatforms (now required)
		if (usesPlatforms === undefined || usesPlatforms === null) {
			newErrors.usesPlatforms = t("validation.selectOption");
		}

		// Validate phone number if required by communication channel
		if (isPhoneRequired) {
			if (isPhoneEmpty(phoneNumber)) {
				newErrors.phoneNumber = t("validation.required");
			} else if (!isPhoneValid(phoneNumber)) {
				newErrors.phoneNumber = t("validation.invalidPhoneNumber");
			}
		}

		// Validate LinkedIn URL if LinkedIn is selected
		if (selectedCommunicationChannels.includes(CommunicationChannel.LinkedInMessaging)) {
			if (!linkedInUrl) {
				newErrors.linkedInUrl = t("validation.required");
			} else if (!linkedInUrl.includes("linkedin.com")) {
				newErrors.linkedInUrl = t("validation.invalidLinkedInUrl");
			}
		}

		setErrors(newErrors);
		const isValid = Object.keys(newErrors).length === 0;
		return isValid;
	};

	// Handle form submission (both draft and final)
	const handleSubmit = async (e: React.FormEvent, isDraft: boolean = false) => {
		e.preventDefault();

		// If already submitting, prevent multiple submissions
		if (isMutating) {
			return;
		}

		// Validate the form - even for drafts to check LinkedIn URL
		const isValid = validateForm(isDraft);
		if (!isValid) {
			// For both draft and final submissions, stop if validation fails
			// Scroll to first error field and focus it
			setTimeout(() => {
				// Look for error fields using multiple selectors
				const firstErrorField = document.querySelector(
					'[aria-invalid="true"], .Mui-error input, .Mui-error textarea, .Mui-error [role="combobox"]'
				) as HTMLElement;

				if (firstErrorField) {
					// Scroll to the field
					firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });

					// Focus on the input element
					if (
						firstErrorField.tagName === "INPUT" ||
						firstErrorField.tagName === "TEXTAREA" ||
						firstErrorField.getAttribute("role") === "combobox"
					) {
						firstErrorField.focus();
					} else {
						// If we selected a wrapper, find the actual input inside
						const inputElement = firstErrorField
							.closest(".MuiFormControl-root")
							?.querySelector('input, textarea, [role="combobox"]') as HTMLElement;
						inputElement?.focus();
					}
				}
			}, 100);
			return;
		}

		try {
			// Check if we have an existing profile (either from current user or clientToEdit)
			const profileToUse = clientToEdit || clientProfile;

			if (profileToUse || clientExists) {
				// Update existing profile (PUT)
				const updateData: ClientUpdateRequest = {
					jobTitle,
					companyId: companyId || undefined,
					phoneNumber: phoneNumber || undefined,
					freelancerFrequency: selectedFrequency as FreelancerFrequency,
					usesPlatforms,
					preferredCommunicationChannels: selectedCommunicationChannels,
					linkedInUrl,
					onboardingCompleted: !isDraft,
					freelancerExpertiseIds: selectedExpertises,
					businessDomainIds: selectedDomains.map(d => d.id),
					toolIds: selectedTools
				};

				const userId = clientToEdit ? clientToEdit.userId : undefined;
				await updateClientProfileMutation.mutateAsync({ clientData: updateData, userId });
				setAlertSeverity("success");
				setAlertMessage(
					isDraft ? t("clientProfile.draftSaved") : t("clientProfile.updateSuccess")
				);
				setAlertOpen(true);
			} else {
				// Create new profile (POST) - only on first save
				const createData: ClientCreateRequest = {
					jobTitle,
					companyId: companyId || undefined,
					phoneNumber: phoneNumber || undefined,
					freelancerFrequency: selectedFrequency as FreelancerFrequency,
					usesPlatforms: usesPlatforms!,
					preferredCommunicationChannels: selectedCommunicationChannels,
					linkedInUrl,
					onboardingCompleted: !isDraft,
					freelancerExpertiseIds: selectedExpertises,
					businessDomainIds: selectedDomains.map(d => d.id),
					toolIds: selectedTools
				};

				await createClientProfileMutation.mutateAsync(createData);
				setClientExists(true); // Mark that client now exists for future saves
				setAlertSeverity("success");
				setAlertMessage(
					isDraft ? t("clientProfile.draftSaved") : t("clientProfile.createSuccess")
				);
				setAlertOpen(true);
			}

			// If this was an onboarding final submission, redirect to projects page
			if (isOnboarding && !isDraft) {
				setTimeout(() => {
					navigate("/projects");
				}, 1500);
			}
		} catch (error) {
			setAlertSeverity("error");
			setAlertMessage(t("clientProfile.saveError"));
			setAlertOpen(true);
		}
	};

	// Helper function to handle field changes
	const handleFieldChange = (field: string, value: any) => {
		switch (field) {
			case "jobTitle":
				setJobTitle(value);
				break;
			case "companyId":
				setCompanyId(value);
				break;
			case "phoneNumber":
				// Only update if the value is different to avoid loops
				if (value !== phoneNumber) {
					setPhoneNumber(value);
				}
				break;
			case "selectedFrequency":
				setSelectedFrequency(value);
				break;
			case "usesPlatforms":
				setUsesPlatforms(value);
				break;
			case "communicationChannels":
				setSelectedCommunicationChannels(value);
				break;
			case "linkedInUrl":
				setLinkedInUrl(value);
				break;
			case "selectedExpertises":
				setSelectedExpertises(value);
				break;
			case "selectedDomains":
				setSelectedDomains(value);
				break;
			case "selectedTools":
				setSelectedTools(value);
				break;
		}

		// Clear error when field is edited (some fields store their error under a different key)
		const fieldToErrorKey: Record<string, string> = {
			selectedExpertises: "freelancerExpertises",
			selectedDomains: "businessDomains",
			selectedFrequency: "freelancerFrequency"
		};
		const errorKey = fieldToErrorKey[field] ?? field;
		if (errors[errorKey]) {
			setErrors(prev => ({ ...prev, [errorKey]: "" }));
		}
	};

	const getExpertiseNameById = (id: number) => {
		const expertise = expertises?.find(exp => exp.id === id);
		return expertise ? t(`Expertises.${expertise.name}`) : "";
	};

	const getToolNameById = (id: number) => {
		const tool = tools?.find(tool => tool.id === id);
		return tool ? tool.label : "";
	};

	// Get the profile data to use (either clientToEdit for CSM/Admin editing, or clientProfile for client's own profile)
	const profileToDisplay = clientToEdit || clientProfile;

	return {
		// Form data - use client's user data when editing someone else's profile, otherwise use auth user data
		firstName: profileToDisplay?.user?.firstName || user?.firstName || "",
		lastName: profileToDisplay?.user?.lastName || user?.lastName || "",
		email: profileToDisplay?.user?.email || user?.email || "",
		jobTitle,
		companyId,
		phoneNumber,
		selectedFrequency,
		usesPlatforms,
		selectedCommunicationChannels,
		linkedInUrl,
		selectedExpertises,
		selectedDomains,
		selectedTools,

		// Company-related
		selectedCompany,
		searchedCompanies,
		isSearchingCompanies,
		companySearchTerm,
		companyDialogOpen,
		createdCompanyIdRef,
		canChangeCompany,
		canCreateNewCompany,
		hasCreatedCompany,

		// UI state
		errors,
		alertOpen,
		alertMessage,
		alertSeverity,
		isLoading,
		isMutating,
		dataLoadingComplete,
		isPhoneRequired,
		clientExists,

		// Data
		expertises,
		tools,
		businessDomains,
		communicationChannels,
		frequencies,

		// Handlers
		handleFieldChange,
		handleSubmit,
		setCompanySearchTerm,
		setSelectedCompany,
		setCompanyDialogOpen,
		setAlertOpen,
		setAlertMessage,
		setAlertSeverity,
		getExpertiseNameById,
		getToolNameById
	};
};
